<template>
  <div class="add-branch-page" dir="rtl">

    <div class="ab-header">
      <h2 class="ab-header__title">إضافة مكان</h2>
      <p class="ab-header__sub">أدخل بيانات المكان وسيتم إرسالها للمراجعة</p>
    </div>

    <form class="ab-form" @submit.prevent="submit" novalidate>

      <!-- ══════════════════════════════════════
           SECTION 1 — اسم المكان
      ══════════════════════════════════════ -->
      <div class="ab-section">
        <div class="ab-section__head">
          <h3 class="ab-section__title">اسم المكان</h3>
          <span class="ab-section__hint">أحدهما مطلوب على الأقل</span>
        </div>

        <div v-if="!hasName && (form.company_name_ar !== '' || form.company_name_en !== '')" class="ab-name-warn">
          <i class="fa fa-exclamation-triangle"></i>
          يجب إدخال الاسم بالعربي أو الإنجليزي على الأقل
        </div>

        <div class="ab-grid">
          <div class="ab-field ab-field--half">
            <label class="ab-field__label">
              الاسم بالعربي
              <span class="ab-field__tag">أحدهما مطلوب</span>
            </label>
            <input
              class="ab-field__input"
              v-model.trim="form.company_name_ar"
              type="text"
              placeholder="مثال: مقهى النخيل"
              @input="syncPreferredName"
            />
          </div>
          <div class="ab-field ab-field--half">
            <label class="ab-field__label">
              الاسم بالإنجليزي
              <span class="ab-field__tag">أحدهما مطلوب</span>
            </label>
            <input
              class="ab-field__input"
              v-model.trim="form.company_name_en"
              type="text"
              placeholder="e.g. Al Nakheel Cafe"
              dir="ltr"
              style="text-align:left"
              @input="syncPreferredName"
            />
          </div>
        </div>

        <!-- Display-name preview (auto: AR if filled, otherwise EN) -->
        <div v-if="form.company_name" class="ab-name-preview">
          <span class="ab-name-preview__label">سيظهر في التطبيق كـ</span>
          <span class="ab-name-preview__value">{{ form.company_name }}</span>
        </div>
      </div>

      <!-- ══════════════════════════════════════
           SECTION 2 — الموقع الجغرافي
      ══════════════════════════════════════ -->
      <div class="ab-section">
        <div class="ab-section__head">
          <h3 class="ab-section__title">الموقع الجغرافي</h3>
        </div>

        <div class="ab-grid">
          <div class="ab-field ab-field--third">
            <label class="ab-field__label">الدولة <span class="ab-field__req">مطلوب</span></label>
            <select class="ab-field__select" v-model="form.country">
              <option disabled value="">اختر الدولة</option>
              <option v-for="c in countries" :key="c.code" :value="c.name_ar">{{ c.name_ar }}</option>
            </select>
          </div>

          <div class="ab-field ab-field--third">
            <label class="ab-field__label">المدينة <span class="ab-field__req">مطلوب</span></label>
            <template v-if="form.country === 'السعودية'">
              <div class="ab-city-combo">
                <input
                  class="ab-field__input ab-city-combo__input"
                  :class="{ 'ab-city-combo__input--invalid': cityQuery && !cityIsValid && !cityDropdownOpen }"
                  v-model="cityQuery"
                  type="text"
                  placeholder="ابحث عن المدينة"
                  autocomplete="off"
                  autocorrect="off"
                  spellcheck="false"
                  @focus="onCityFocus"
                  @click="onCityFocus"
                  @input="onCityFocus"
                  @blur="onCityBlur"
                />
                <button
                  v-if="cityQuery"
                  type="button"
                  class="ab-city-combo__clear"
                  @mousedown.prevent
                  @click="clearCity"
                  aria-label="مسح"
                >
                  <i class="fa fa-times"></i>
                </button>
                <i
                  v-else-if="cityIsValid"
                  class="fa fa-check-circle ab-city-combo__check"
                ></i>

                <div
                  v-if="cityDropdownOpen && filteredCities.length"
                  class="ab-city-combo__dropdown"
                  @mousedown.prevent
                >
                  <button
                    v-for="c in filteredCities"
                    :key="c.id || c.name_ar"
                    type="button"
                    class="ab-city-combo__option"
                    :class="{ 'ab-city-combo__option--on': form.city === c.name_ar }"
                    @click="pickCity(c)"
                  >
                    {{ c.name_ar }}
                  </button>
                </div>

                <p
                  v-if="cityQuery && !cityIsValid && !cityDropdownOpen"
                  class="ab-city-combo__hint"
                >
                  اختر مدينة من القائمة
                </p>
              </div>
            </template>
            <select v-else class="ab-field__select" v-model="form.city" :disabled="!citiesForCountry.length">
              <option disabled value="">اختر المدينة</option>
              <option v-for="ct in citiesForCountry" :key="ct.name_ar" :value="ct.name_ar">{{ ct.name_ar }}</option>
            </select>
          </div>

          <div class="ab-field ab-field--third">
            <label class="ab-field__label">الحي <span class="ab-field__req">مطلوب</span></label>
            <input
              class="ab-field__input"
              v-model.trim="form.district"
              type="text"
              placeholder="مثال: حي العليا"
              required
            />
          </div>
        </div>
      </div>

      <!-- ══════════════════════════════════════
           SECTION 3 — تحديد الموقع على الخريطة
      ══════════════════════════════════════ -->
      <div class="ab-section">
        <div class="ab-section__head">
          <h3 class="ab-section__title">موقع المكان على الخريطة</h3>
          <span class="ab-section__hint">مطلوب</span>
        </div>

        <div class="ab-map-wrap">
          <div id="inline-map-container" class="ab-map-inline"></div>

          <div v-if="hasCoords" class="ab-map-coords-badge">
            <i class="fa fa-check-circle"></i>
            {{ coordsLabel }}
          </div>
          <div v-else class="ab-map-hint-overlay">
            <i class="fa fa-hand-pointer"></i>
            اضغط على الخريطة لتحديد الموقع
          </div>
        </div>

        <span v-if="locationStatus" class="ab-location-status">
          <i class="fa fa-info-circle"></i>
          {{ locationStatus }}
        </span>

        <div class="ab-maps-link">
          <label class="ab-field__label">الصق رابط المكان من Google Maps</label>
          <div class="ab-maps-link__row">
            <input
              class="ab-field__input"
              v-model.trim="form.location_URL"
              type="url"
              maxlength="2048"
              placeholder="https://maps.app.goo.gl/..."
              dir="ltr"
              style="text-align:left"
              @input="clearLinkState"
              @keyup.enter.prevent="resolveLink"
            />
            <i v-if="linkStatus === 'ok'" class="fa fa-check-circle ab-maps-link__icon ab-maps-link__icon--ok"></i>
            <i v-else-if="linkStatus === 'fail'" class="fa fa-exclamation-circle ab-maps-link__icon ab-maps-link__icon--fail"></i>
          </div>
          <button
            type="button"
            class="ab-maps-link__btn"
            :disabled="resolvingLink || !form.location_URL"
            @click="resolveLink"
          >
            <i class="fa" :class="resolvingLink ? 'fa-spinner fa-spin' : 'fa-location-dot'"></i>
            {{ resolvingLink ? "جاري تحديد الموقع..." : "تحديد الموقع من الرابط" }}
          </button>
          <p class="ab-field__hint">
            افتح المكان في Google Maps → "مشاركة" → نسخ الرابط، ثم الصقه هنا واضغط «تحديد الموقع»
          </p>
          <p v-if="linkStatus === 'fail' && linkError" class="ab-maps-link__fail">
            {{ linkError }}
          </p>
        </div>

        <div class="ab-gps-row">
          <button
            type="button"
            class="ab-location-btn"
            @click="getDeviceLocation"
            :disabled="locating"
          >
            <i class="fa" :class="locating ? 'fa-spinner fa-spin' : 'fa-crosshairs'"></i>
            {{ locating ? "جاري التحديد..." : "أنا في المكان الآن" }}
          </button>
          <span class="ab-field__hint">استخدم هذا الخيار فقط إذا كنت داخل المكان حالياً</span>
        </div>
      </div>

      <!-- ══════════════════════════════════════
           SECTION 4 — صورة المكان
      ══════════════════════════════════════ -->
      <div class="ab-section">
        <div class="ab-section__head">
          <h3 class="ab-section__title">
            صورة المكان
            <span class="ab-field__req">مطلوبة</span>
          </h3>
        </div>

        <div v-if="imagePreview" class="ab-image-preview">
          <img :src="imagePreview" alt="preview" />
          <div class="ab-image-preview__meta">
            <span class="ab-field__hint">{{ imageName }}</span>
            <button type="button" class="ab-btn-remove" @click="clearImage">
              <i class="fa fa-times"></i>
              إزالة
            </button>
          </div>
        </div>

        <div class="ab-image-actions">
          <label class="ab-image-btn ab-image-btn--camera">
            <input type="file" accept="image/*" capture="environment" @change="onCameraChange" hidden />
            <i class="fa fa-camera"></i>
            <span>صوّر الآن</span>
          </label>
          <label class="ab-image-btn ab-image-btn--gallery">
            <input type="file" accept="image/*" @change="onGalleryChange" hidden />
            <i class="fa fa-image"></i>
            <span>اختر من المعرض</span>
          </label>
        </div>
      </div>

      <!-- ══════════════════════════════════════
           SECTION 5 — ملاحظة
      ══════════════════════════════════════ -->
      <div class="ab-section">
        <div class="ab-field">
          <label class="ab-field__label">
            ملاحظة
            <span class="ab-field__tag">اختياري</span>
          </label>
          <textarea
            class="ab-field__textarea"
            v-model.trim="form.note"
            rows="3"
            placeholder="أي معلومات إضافية تريد إضافتها"
          ></textarea>
        </div>
      </div>

      <!-- ── Error ── -->
      <div v-if="error" class="ab-alert ab-alert--error">
        <i class="fa fa-exclamation-circle"></i>
        {{ error }}
      </div>

      <!-- ── Sticky submit ── -->
      <div class="ab-submit-bar">
        <p v-if="!canSubmit && missingRequirements.length" class="ab-missing">
          <i class="fa fa-circle-info"></i>
          أكمل: {{ missingRequirements.join(" · ") }}
        </p>
        <button class="ab-submit" type="submit" :disabled="loading || !canSubmit">
          <i class="fa" :class="loading ? 'fa-spinner fa-spin' : 'fa-paper-plane'"></i>
          {{ loading ? "جاري الإرسال..." : "إرسال الطلب" }}
        </button>
      </div>

    </form>

  </div>
</template>

<script setup>
import { useAddBranchForm } from "@/composables/useAddBranchForm";

const {
  form, countries, saCities, citiesForCountry, cityQuery,
  cityDropdownOpen, filteredCities, cityIsValid,
  loading, locating, canSubmit, missingRequirements, error,
  locationStatus, hasCoords, hasName, coordsLabel,
  resolvingLink, linkStatus, linkError, resolveLink, clearLinkState,
  imageName, imagePreview,
  syncPreferredName,
  pickCity, clearCity, onCityFocus, onCityBlur,
  onCameraChange, onGalleryChange, clearImage,
  getDeviceLocation,
  submit,
} = useAddBranchForm();
</script>

<style scoped lang="scss">
.add-branch-page {
  background: var(--color-bg);
  padding: calc(var(--space-3) + env(safe-area-inset-top, 0px)) var(--page-padding-x) var(--page-padding-bottom);
  direction: rtl;
  text-align: right;
}

.ab-header {
  padding: var(--space-3) 0 var(--space-4);
  max-width: 880px; margin: 0 auto;
  &__title { margin: 0 0 4px; font-size: 1.15rem; font-weight: 800; color: var(--color-text-1); letter-spacing: -.02em; }
  &__sub   { margin: 0; font-size: 0.82rem; color: var(--color-text-3); }
}

.ab-form {
  display: flex; flex-direction: column; gap: var(--space-4);
  max-width: 880px; margin: 0 auto;
}

.ab-section {
  background: var(--color-panel); border: 1px solid var(--color-border);
  border-radius: var(--radius-lg); padding: var(--space-4);
  display: flex; flex-direction: column; gap: var(--space-3);

  &__head { display: flex; align-items: center; justify-content: space-between; gap: var(--space-2); flex-wrap: wrap; }
  &__title { margin: 0; font-size: 0.95rem; font-weight: 800; color: var(--color-text-1); display: flex; align-items: center; gap: var(--space-2); }
  &__hint  { font-size: 0.75rem; color: var(--color-text-4); }
}

.ab-grid { display: flex; flex-wrap: wrap; gap: var(--space-3); }

.ab-field {
  display: flex; flex-direction: column; gap: var(--space-1); flex: 1; min-width: 200px;
  &--half  { min-width: 140px; }
  &--third { min-width: 120px; }

  &__label { font-size: 0.82rem; font-weight: 600; color: var(--color-text-2); display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; }
  &__req   { color: var(--color-closed); font-size: 0.72rem; }
  &__tag   { font-size: 0.65rem; color: var(--color-text-4); background: var(--color-card); border: 1px solid var(--color-border); padding: 1px 6px; border-radius: var(--radius-full); }
  &__hint  { font-size: 0.72rem; color: var(--color-text-4); }

  &__input, &__select, &__textarea {
    background: var(--color-card); border: 1px solid var(--color-border-lit);
    border-radius: var(--radius-md); padding: 11px var(--space-3);
    color: var(--color-text-1); font-family: var(--font-primary); font-size: 0.90rem;
    transition: border-color var(--transition-base); width: 100%; box-sizing: border-box;
    &:focus { outline: none; border-color: var(--color-signal); box-shadow: 0 0 0 3px var(--color-signal-soft); }
    &::placeholder { color: var(--color-text-4); }
    option { background: var(--color-card); }
  }

  &__textarea { resize: vertical; min-height: 80px; }
}

// ── City combobox (custom — no iOS-killing datalist) ─
.ab-city-combo {
  position: relative;

  &__input {
    width: 100%;
    padding-left: 36px; // leave room for the validity check / clear button on the visual left

    &--invalid {
      border-color: var(--color-closed) !important;
    }
  }

  &__check {
    position: absolute;
    top: 50%;
    left: 12px;
    transform: translateY(-50%);
    color: var(--color-open, #8FA37A);
    font-size: 0.95rem;
    pointer-events: none;
  }

  &__clear {
    position: absolute;
    top: 50%;
    left: 8px;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: none;
    background: var(--color-card);
    color: var(--color-text-3);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.70rem;
    transition: background-color 0.12s, color 0.12s;

    &:hover, &:active {
      background: var(--color-closed-soft);
      color: var(--color-closed);
    }
  }

  &__dropdown {
    position: absolute;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 50;
    max-height: 260px;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;

    background: var(--color-panel);
    border: 1px solid var(--color-border-lit);
    border-radius: var(--radius-md);
    box-shadow: 0 12px 28px -8px rgba(0, 0, 0, 0.35);

    scrollbar-width: thin;
    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-thumb { background: var(--color-border-lit); border-radius: 2px; }
  }

  &__option {
    display: block;
    width: 100%;
    padding: 12px var(--space-3);
    background: transparent;
    border: none;
    border-bottom: 1px solid var(--color-border);
    text-align: right;
    font-family: var(--font-primary);
    font-size: 0.88rem;
    color: var(--color-text-1);
    cursor: pointer;
    transition: background-color 0.12s, color 0.12s;
    direction: rtl;

    &:last-child { border-bottom: none; }
    &:hover,
    &:active   { background: var(--color-card); color: var(--color-signal); }

    &--on {
      background: var(--color-signal-soft);
      color: var(--color-signal);
      font-weight: 700;
    }
  }

  &__hint {
    margin: 4px 0 0;
    font-size: 0.72rem;
    color: var(--color-closed);
    display: flex; align-items: center; gap: 4px;
    &::before {
      content: "⚠";
      font-size: 0.78rem;
    }
  }
}

// ── Name warn / preview ───────────────────────
.ab-name-warn {
  display: flex; align-items: center; gap: var(--space-2);
  padding: var(--space-2) var(--space-3); border-radius: var(--radius-md);
  background: rgba(245,166,35,0.1); border: 1px solid rgba(245,166,35,0.3);
  color: var(--color-amber); font-size: 0.80rem;
}

.ab-name-preview {
  display: flex; align-items: center; gap: var(--space-2);
  padding: var(--space-2) var(--space-3); border-radius: var(--radius-md);
  background: var(--color-card); border: 1px solid var(--color-border);
  &__label { font-size: 0.72rem; color: var(--color-text-4); }
  &__value { font-size: 0.88rem; font-weight: 700; color: var(--color-text-1); }
}

// ── Inline map ────────────────────────────────
.ab-map-wrap {
  position: relative;
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: 1px solid var(--color-border);
}

.ab-map-inline {
  width: 100%;
  height: 280px;
  background: var(--color-card);
}

.ab-map-coords-badge {
  position: absolute; bottom: var(--space-2); right: var(--space-2);
  background: rgba(52,211,153,0.9); color: white;
  font-size: 0.70rem; font-family: var(--font-mono); font-weight: 600;
  padding: 4px 10px; border-radius: var(--radius-full);
  display: flex; align-items: center; gap: 5px;
  backdrop-filter: blur(4px);
}

.ab-map-hint-overlay {
  position: absolute; bottom: var(--space-2); right: 50%; transform: translateX(50%);
  background: rgba(0,0,0,0.55); color: white;
  font-size: 0.75rem; font-weight: 600;
  padding: 5px 14px; border-radius: var(--radius-full);
  display: flex; align-items: center; gap: 6px;
  backdrop-filter: blur(4px); white-space: nowrap;
  pointer-events: none;
}

.ab-location-status {
  display: flex; align-items: center; gap: var(--space-2);
  font-size: 0.78rem; color: var(--color-text-3);
  i { color: var(--color-signal); font-size: 0.72rem; }
}

.ab-location-btn {
  display: inline-flex; align-items: center; gap: var(--space-2);
  padding: 10px var(--space-4); border-radius: var(--radius-md);
  border: 1px solid var(--color-border-lit); background: var(--color-card);
  color: var(--color-text-2); font-family: var(--font-primary);
  font-size: 0.85rem; font-weight: 600; cursor: pointer;
  transition: all var(--transition-base); align-self: flex-start;
  &:hover:not(:disabled) { border-color: var(--color-signal); color: var(--color-signal); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

// ── Google Maps URL (first-class option) ──────
.ab-maps-link {
  display: flex; flex-direction: column; gap: var(--space-1);

  &__row {
    position: relative;

    .ab-field__input { padding-left: 38px; }
  }

  &__icon {
    position: absolute;
    top: 50%; left: 12px;
    transform: translateY(-50%);
    font-size: 0.95rem;
    color: var(--color-text-4);
    pointer-events: none;

    &--ok   { color: var(--color-open, #8FA37A); }
    &--fail { color: var(--color-closed, #D86545); }
  }

  &__fail {
    margin: 0; font-size: 0.75rem; color: var(--color-closed);
  }

  &__btn {
    display: inline-flex; align-items: center; gap: var(--space-2);
    align-self: flex-start;
    padding: 9px var(--space-4);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border-lit);
    background: var(--color-card);
    color: var(--color-text-2); font-family: var(--font-primary);
    font-size: 0.8rem; font-weight: 600; cursor: pointer;
    transition: all var(--transition-base);
    &:hover:not(:disabled) { border-color: var(--color-signal); color: var(--color-signal); }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }
}

.ab-gps-row {
  display: flex; align-items: center; gap: var(--space-3); flex-wrap: wrap;
}

// ── Image ─────────────────────────────────────
.ab-image-preview {
  border-radius: var(--radius-md); overflow: hidden;
  border: 2px solid var(--color-open, #8FA37A);
  box-shadow: 0 0 0 3px rgba(143, 163, 122, 0.15);
  img {
    display: block;
    width: 100%;
    max-height: 280px;
    object-fit: cover;
    object-position: center;
  }
  &__meta { display: flex; align-items: center; justify-content: space-between; padding: var(--space-2) var(--space-3); background: var(--color-card); }
}

.ab-image-actions { display: flex; gap: var(--space-3); flex-wrap: wrap; }

.ab-image-btn {
  flex: 1; min-width: 120px; display: flex; flex-direction: column;
  align-items: center; justify-content: center; gap: var(--space-2);
  padding: var(--space-4); border-radius: var(--radius-lg);
  font-family: var(--font-primary); font-size: 0.85rem; font-weight: 600;
  cursor: pointer; transition: all var(--transition-base);
  i { font-size: 1.4rem; }

  &--camera { background: var(--color-signal-soft); border: 1px dashed rgba(59,158,255,0.4); color: var(--color-signal); &:hover { background: rgba(59,158,255,0.18); border-color: var(--color-signal); } }
  &--gallery { background: var(--color-card); border: 1px dashed var(--color-border-lit); color: var(--color-text-2); &:hover { border-color: var(--color-border-lit); color: var(--color-text-1); } }
}

.ab-btn-remove {
  display: inline-flex; align-items: center; gap: var(--space-1);
  padding: 5px 10px; border-radius: var(--radius-md);
  border: 1px solid rgba(248,113,113,0.3); background: var(--color-closed-soft);
  color: var(--color-closed); font-family: var(--font-primary);
  font-size: 0.75rem; font-weight: 600; cursor: pointer;
  transition: opacity var(--transition-base); &:hover { opacity: 0.8; }
}

// ── Alerts ────────────────────────────────────
.ab-alert {
  display: flex; align-items: center; gap: var(--space-2);
  padding: var(--space-3) var(--space-4); border-radius: var(--radius-md);
  font-size: 0.82rem; font-weight: 500;
  &--error { background: var(--color-closed-soft); border: 1px solid rgba(248,113,113,0.3); color: var(--color-closed); }
}

// ── Submit ────────────────────────────────────
.ab-submit-bar {
  position: sticky; bottom: 72px; padding: var(--space-3) 0;
  background: linear-gradient(to top, var(--color-bg) 65%, transparent);
  z-index: 10;
}

.ab-missing {
  margin: 0 0 var(--space-2);
  display: flex; align-items: center; gap: var(--space-2);
  font-size: 0.74rem; color: var(--color-text-3);
  i { color: var(--color-signal); font-size: 0.72rem; flex-shrink: 0; }
}

.ab-submit {
  display: flex; align-items: center; justify-content: center;
  gap: var(--space-2); width: 100%; padding: 14px;
  border-radius: var(--radius-lg); border: none;
  background: linear-gradient(135deg, var(--color-signal, #E2B673), #C29250);
  color: #1a1410; font-family: var(--font-display); font-size: 0.95rem;
  font-weight: 800; cursor: pointer;
  box-shadow: 0 12px 30px -10px rgba(226, 182, 115, 0.5);
  transition: opacity var(--transition-base);
  &:hover:not(:disabled) { opacity: 0.92; }
  &:disabled {
    background: var(--color-panel); color: var(--color-text-4);
    box-shadow: none; cursor: not-allowed;
    border: 1px solid var(--color-border);
  }
}
</style>